import React, { useState, useEffect } from 'react';
import { useThread } from './ThreadContext';
import ChatContainer from './ChatContainer';
import ChatInput from './ChatInput';
import BouncingDotsLoader from './BouncingDotsLoader';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { ConfigurationPane } from './ConfigurationPane';
import { useConfiguration } from './hooks/useConfiguration';

interface SearchEvent {
    completed: boolean;
    results: string[];
    search_message: string;
}

interface Form {
    form_title: string;
    form_field_name: string;
    form_instructions: string;
}

const ChatComponent: React.FC = () => {
    const { threadId, setThreadId } = useThread();
    const [messages, setMessages] = useState<{ id: string, role: string, content: string, searchEvent: SearchEvent | null }[]>([]);
    const [input, setInput] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [currentMessage, setCurrentMessage] = useState<string>('');
    const [currentSearchEvent, setCurrentSearchEvent] = useState<SearchEvent | null>(null);
    const [form, setForm] = useState<Form | null>(null);
    const [isConfiguring, setIsConfiguring] = useState(false);
    const { config } = useConfiguration();
    useEffect(() => {
        if (!threadId) {
            createThread();
        }
    }, [threadId]);

    const createThread = async () => {
        const response = await fetch("api/create-thread", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                personal_information: config.personalizedInformation,
                personalized_response_behavior: config.personalizedResponseBehavior
            }),
        });
        const data = await response.json();
        setThreadId(data.threadId);
    };

    const sendMessage = async () => {
        if (!input.trim()) return;

        setIsSending(true);

        const newMessage = { id: `${Date.now()}`, role: 'user', content: input, searchEvent: null };
        setMessages([...messages, newMessage]);
        setInput('');

        const response = await fetch("api/chat", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ message: input, threadId }),
        });

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let done = false;
        let buffer = '';
        let accumulatedText = '';

        while (!done) {
            const { value, done: doneReading } = await reader?.read()!;
            done = doneReading;
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            const lines = buffer.split('\n');
            buffer = lines.pop()!; // Keep the last partial line in the buffer

            for (const line of lines) {
                if (line.trim()) {
                    const parsed = JSON.parse(line);
                    if (parsed.type === 'text_delta') {
                        accumulatedText += parsed.data;
                        setCurrentMessage(accumulatedText);
                    } else if (parsed.type === 'search_started') {
                        setCurrentSearchEvent(parsed.data)
                    } else if (parsed.type === 'search_completed') {
                        setCurrentSearchEvent(parsed.data)
                    } else if (parsed.type === 'show_form_to_fill_out') {
                        setForm(parsed.data);
                    }
                }
            }
        }

        setIsSending(false);

        if (done) {
            setMessages((prevMessages) => [
                ...prevMessages,
                { id: `${Date.now()}`, role: 'assistant', content: accumulatedText, searchEvent: currentSearchEvent },
            ]);
            setCurrentMessage('');
            setCurrentSearchEvent(null);
        }
    };
    return (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-16 sm:top-16 sm:absolute sm:right-0 sm:mr-4 bg-white p-6 sm:rounded-lg sm:border sm:border-gray-200 flex flex-col sm:w-[36rem] lg:w-[44rem] sm:max-w-full sm:h-auto sm:shadow-md">
            <div className="flex-none flex items-center justify-between">
                <div className="flex justify-between items-center w-full">
                    <div className="flex items-center p-4 bg-white rounded-lg">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Abraxas_Informatik_AG_Logo.png" alt="Logo" className="h-12 mr-8" />
                        <h1 className="text-xl font-semibold">Abraxas Mate</h1>

                    </div>
                    {!isConfiguring &&
                        <Cog6ToothIcon width={36} style={{ cursor: 'pointer' }} onClick={() => setIsConfiguring(true)} />
                    }
                </div>
            </div>
            {isConfiguring &&
                <ConfigurationPane />
            }
            {!isConfiguring &&
                <>
                    {threadId ? (
                        <>
                            <ChatContainer
                                messages={messages}
                                currentMessage={currentMessage}
                                currentSearchEvent={currentSearchEvent}
                                addMessage={(message) => setMessages((prevMessages) => [...prevMessages, message])}
                                setInput={setInput}
                                sendMessage={sendMessage}
                            />
                            {isSending && !currentMessage &&
                                <BouncingDotsLoader />
                            }
                            {form ? (
                                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                                    <div className="mb-4 flex items-center gap-x-6">
                                        <img className="h-12 w-12 rounded-full" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&amp;ixid=eyJhcHBfaWQiOjEyMDd9&amp;auto=format&amp;fit=facearea&amp;facepad=2&amp;w=256&amp;h=256&amp;q=80" alt="" />
                                        <div>
                                            <h3 className="text-base font-semibold leading-7 tracking-tight text-gray-900">{form.form_title}</h3>
                                        </div>
                                    </div>
                                    <form className="space-y-6" action="#" method="POST">
                                        <div>
                                            <label htmlFor="genericField" className="block text-sm font-medium leading-6 text-gray-900">{form.form_field_name}</label>
                                            <div className="mt-2">
                                                <input id="genericField" name="genericField" type="text" placeholder={form.form_field_name} required className="block px-2 w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" />
                                            </div>
                                        </div>
                                        <div>
                                            <button type="submit" className="flex w-full justify-center rounded-md bg-[rgb(68,68,68)] px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-[rgb(193,0,0)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">Einreichen</button>
                                        </div>
                                    </form>
                                </div>
                            ) : (
                                <ChatInput input={input} setInput={setInput} sendMessage={sendMessage} isSending={isSending} />
                            )}
                        </>
                    ) : (
                        <div className="flex justify-center items-center h-full">
                            <p className="text-gray-500">Konversation wird gestartet...</p>
                        </div>
                    )}
                </>
            }
        </div>
    );
};

export default ChatComponent;
